/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { Button, FlexRow, FlexCol, Radio, Text, Icon, DividerNew } from 'sarsaparilla';

import { SavedCardRadioOption } from './SavedCardRadioOption';
import { getIsCardExpired } from './SavedCreditCardCard.utils';
import type { SavedCard } from '../../api/digital-wallet-types';

interface SavedCreditCardFormProps {
    onSubmit: React.FormEventHandler<HTMLFormElement>;
    sortedSavedCards: SavedCard[];
    selectedCardToken: string | undefined;
    setSelectedCardToken: (token: string | undefined) => void;
    isInternal?: boolean | false;
    submitButtonText?: string;
    simple?: boolean;
    showBorder?: boolean;
}

export function SavedCreditCardForm({
    onSubmit,
    sortedSavedCards,
    selectedCardToken,
    setSelectedCardToken,
    isInternal,
    submitButtonText = 'Next',
    simple = false,
    showBorder = false,
}: SavedCreditCardFormProps) {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCardToken(event.target.value);
    };

    const isCollapsed = simple && !!selectedCardToken;
    const cardsToShow = isCollapsed
        ? sortedSavedCards.filter((card) => card.wallet_token === selectedCardToken)
        : sortedSavedCards;

    return (
        <form
            onSubmit={onSubmit}
            className={cx({ 'saved-card-form-bordered': showBorder })}
        >
            {cardsToShow.map((card, index) => {
                const isExpired = getIsCardExpired(card.card_expiration_date);

                if (isCollapsed) {
                    return (
                        <FlexRow
                            key={card.entry_id}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <FlexCol>
                                <SavedCardRadioOption
                                    aria-label={card.name}
                                    entryId={card.entry_id}
                                    last4={card.card_last_four}
                                    brand={card.card_brand}
                                    expDate={card.card_expiration_date}
                                    nickname={card.name}
                                    isDefault={false}
                                    className="radio-label"
                                />
                            </FlexCol>
                            <FlexCol xs="auto" className="change-payment-method">
                                <Button
                                    appearance="link"
                                    size="sm"
                                    onClick={() => setSelectedCardToken(undefined)}
                                >
                                    Change Payment Method
                                </Button>
                            </FlexCol>
                        </FlexRow>
                    );
                }

                return (
                    <React.Fragment key={card.entry_id}>
                        <Radio
                            className={cx('radio', {
                                first: index === 0,
                                expired: isExpired,
                                selected: selectedCardToken === card.wallet_token,
                            })}
                            isChecked={selectedCardToken === card.wallet_token}
                            name="cardWalletToken"
                            value={card.wallet_token}
                            onChange={handleChange}
                            isDisabled={isExpired}
                            label={
                                <SavedCardRadioOption
                                    aria-label={`${card.name} ${card.is_default ? ' (Default)' : ''}`}
                                    entryId={card.entry_id}
                                    last4={card.card_last_four}
                                    brand={card.card_brand}
                                    expDate={card.card_expiration_date}
                                    nickname={card.name}
                                    isDefault={card.is_default}
                                    className="radio-label"
                                />
                            }
                        />
                        <DividerNew />
                    </React.Fragment>
                );
            })}

            {!isInternal && !isCollapsed && (
                <Text size="sm" align="right" color="gray" className="mt-1">
                    You can add, edit, or remove saved cards from{' '}
                    <a
                        href="/account/wallet"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link-to-account"
                    >
                        Your Account <Icon iconName="chevron-right" size="sm" />
                    </a>{' '}
                    <span className="rec-sr-only">(Opens in new tab)</span>
                </Text>
            )}

            {!simple && (
                <div className="mt-3">
                    <Button
                        isDisabled={!selectedCardToken}
                        type="submit"
                        appearance="primary"
                        size="lg"
                        className="mb-2"
                        gaTrackingId="823720440441"
                    >
                        {submitButtonText}
                    </Button>
                </div>
            )}
        </form>
    );
}
